import React, { useState, useEffect } from "react";
import {
  Card,
  Tooltip,
  Row,
  Col,
  Empty,
  Modal,
  Table,
  Button,
  message,
  Tag,
  Grid,
  Input,
  Select,
  Form,
  Spin,
} from "antd";
import { useTranslation } from "react-i18next";
import {
  UserOutlined,
  MailOutlined,
  TeamOutlined,
  ExportOutlined,
  CrownOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import {
  team_deleteMember,
  team_delete,
  team_deleteByAdmin,
  team_create,
  team_getMemberNotInTeam,
  team_getteamconfig,
  team_updateName,
  team_updateMember,
  team_changeLeader,
  team_updateMemberRole,
  team_getMyTaskRole,
} from "../../../services/team";
import { get_profile } from "../../../services/user";
import { useAuth } from "../../../utils/Auth/AuthWrapper";
import "../styles/index.less";

const { useBreakpoint } = Grid;

const SUPER_ADMIN_ROLE_ID = '99999999-0000-0000-0000-999999999999';

interface Props {
  memberDetail: any[];
  taskId: string;
  currentUserId?: string;
  userRole?: string;
  userRoleId?: string | null;
  isMember?: boolean;
  myTeam?: any;
  onRefresh?: () => void;
  autoOpenCreate?: boolean;
  onAutoOpenCreateDone?: () => void;
}

const TabMember: React.FC<Props> = ({
  memberDetail,
  taskId,
  currentUserId,
  userRole,
  userRoleId,
  isMember,
  myTeam,
  onRefresh,
  autoOpenCreate,
  onAutoOpenCreateDone,
}) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const screens = useBreakpoint();
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    visible: boolean;
    title: string;
    description: string;
    teamName: string;
    onConfirm: () => void;
  }>({ visible: false, title: '', description: '', teamName: '', onConfirm: () => {} });

  const closeConfirmModal = () => setConfirmModal(prev => ({ ...prev, visible: false }));
  const [selectedTeam, setSelectedTeam] = useState<any>(null);

  // Create/Edit team modal
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [editingTeam, setEditingTeam] = useState<any>(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [availableMembers, setAvailableMembers] = useState<any[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [teamConfig, setTeamConfig] = useState({ min_size: 1, team_size: 15, teamname_regex: ".*" });
  const [currentUserTaskRole, setCurrentUserTaskRole] = useState<number | null>(null);
  const [form] = Form.useForm();

  const isMobile = !screens.sm;
  const isTablet = screens.sm && !screens.lg;

  const userTeam = myTeam;

  useEffect(() => {
    if (taskId) {
      team_getMyTaskRole({ task_id: taskId }).then((res) => {
        if (res.status === "success") setCurrentUserTaskRole(res.task_role ?? null);
      }).catch(() => {});
    }
  }, [taskId]);

  useEffect(() => {
    if (autoOpenCreate) {
      openCreateModal();
      onAutoOpenCreateDone?.();
    }
  }, [autoOpenCreate]);

  const isUserLeaderOfTeam = () => {
    if (!currentUserId || !userTeam?.members) return false;
    const userMember = userTeam.members.find(
      (m: any) => m.user_id === currentUserId
    );
    return userMember?.team_role === 1;
  };

  const isUserInSelectedTeam = () => {
    if (!currentUserId || !selectedTeam?.members) return false;
    return selectedTeam.members.some((m: any) => m.user_id === currentUserId);
  };

  const isUserLeaderOfSelectedTeam = () => {
    if (!currentUserId || !selectedTeam?.members) return false;
    const userMember = selectedTeam.members.find(
      (m: any) => m.user_id === currentUserId
    );
    return userMember?.team_role === 1;
  };

  const isTaskAdmin = currentUserTaskRole === 2;

  const isAdminOrSuperAdmin = () =>
    userRoleId === SUPER_ADMIN_ROLE_ID || isTaskAdmin;

  const handleAdminDeleteTeam = (team: any, e: React.MouseEvent) => {
    e.stopPropagation();
    const teamId = team.team_id || team.id;
    const teamName = team.team_name || team.name;
    setConfirmModal({
      visible: true,
      title: "ยืนยันการลบทีม",
      description: "คุณต้องการลบทีม :",
      teamName,
      onConfirm: async () => {
        closeConfirmModal();
        try {
          const response = await team_deleteByAdmin({ task_id: taskId, team_id: teamId });
          if (response.status === "success") {
            message.success("ลบทีมสำเร็จ");
            handleCloseDetailModal();
            onRefresh?.();
          } else {
            message.error(response.message || "ไม่สามารถลบทีมได้");
          }
        } catch (error) {
          message.error("เกิดข้อผิดพลาดในการลบทีม");
        }
      },
    });
  };

  const handleTeamClick = (team: any) => {
    setSelectedTeam(team);
    setIsDetailModalVisible(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalVisible(false);
    setSelectedTeam(null);
  };

  const handleCopyEmail = (email: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(email).then(
      () => message.success("คัดลอกอีเมลสำเร็จ"),
      () => message.error("ไม่สามารถคัดลอกอีเมลได้")
    );
  };

  const getLeaderEmail = () => {
    if (!selectedTeam?.members) return null;
    const leader = selectedTeam.members.find((m: any) => m.team_role === 1);
    return leader?.email || null;
  };

  const getLeaderEmailForTeam = (team: any) => {
    if (!team?.members) return null;
    const leader = team.members.find((m: any) => m.team_role === 1);
    return leader?.email || null;
  };

  // Handle leave team
  const handleLeaveTeam = async () => {
    const targetTeam = selectedTeam || userTeam;
    if (!targetTeam || !currentUserId) return;

    const teamId = targetTeam.team_id || targetTeam.id;
    const memberCount = targetTeam.count_member || targetTeam.members?.length || 0;
    const isLeader = (targetTeam.members || []).find((m: any) => m.user_id === currentUserId)?.team_role === 1;
    const isLastMember = memberCount <= 1;

    // Leader ออกไม่ได้ถ้ายังมีสมาชิกอยู่
    if (isLeader && !isLastMember) {
      message.error("กรุณาให้สมาชิกทุกคนออกจากทีมก่อน จึงจะสามารถออกทีมได้");
      return;
    }

    const teamName = targetTeam.team_name || targetTeam.name;
    setConfirmModal({
      visible: true,
      title: t("Confirm leave team"),
      description: isLeader && isLastMember
        ? "คุณเป็นสมาชิกคนสุดท้าย การออกทีมจะลบทีมนี้ออกด้วย คุณต้องการออกจากทีม :"
        : "คุณต้องการออกจากทีม :",
      teamName,
      onConfirm: async () => {
        closeConfirmModal();
        setLeaveLoading(true);
        try {
          let response;
          if (isLeader && isLastMember) {
            response = await team_delete({ task_id: taskId, team_id: teamId });
          } else {
            response = await team_deleteMember({ task_id: taskId, team_id: teamId, user_id: currentUserId });
          }
          if (response.status === "success") {
            message.success(isLeader && isLastMember ? "ออกทีมและลบทีมสำเร็จ" : t("Left team successfully"));
            handleCloseDetailModal();
            onRefresh?.();
          } else {
            message.error(response.message || t("Cannot leave team"));
          }
        } catch (error) {
          message.error(t("Error leaving team"));
        } finally {
          setLeaveLoading(false);
        }
      },
    });
  };

  const closeModal = () => {
    setIsCreateModalVisible(false);
    setEditingTeam(null);
    form.resetFields();
  };

  // Open create team modal
  const openCreateModal = async () => {
    const currentUser = auth.user;
    setEditingTeam(null);
    setIsCreateModalVisible(true);
    setLoadingMembers(true);
    try {
      const [memberRes, configRes, profileRes] = await Promise.all([
        team_getMemberNotInTeam({ task_id: taskId }),
        team_getteamconfig({ task_id: taskId }),
        get_profile(),
      ]);
      const profile = profileRes?.result?.[0];
      form.setFieldsValue({
        name: "",
        members: [
          {
            user_id: currentUser.user_id,
            name: profile?.user_name || currentUser.user_name || "",
            email: profile?.user_email || "",
            role: 1,
          },
        ],
      });
      if (memberRes.status === "success" && Array.isArray(memberRes.data)) {
        setAvailableMembers(
          memberRes.data.filter((m: any) => m.user_id !== currentUser.user_id)
        );
      }
      if (configRes.status === "success" && configRes.result?.length > 0) {
        const cfg = configRes.result[0];
        setTeamConfig({
          min_size: cfg.min_size ?? 1,
          team_size: cfg.team_size ?? 15,
          teamname_regex: cfg.teamname_regex || ".*",
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMembers(false);
    }
  };

  // Open edit team modal
  const openEditModal = async (team: any) => {
    setEditingTeam(team);
    setIsCreateModalVisible(true);
    setLoadingMembers(true);
    try {
      const [memberRes, configRes] = await Promise.all([
        team_getMemberNotInTeam({ task_id: taskId }),
        team_getteamconfig({ task_id: taskId }),
      ]);
      if (memberRes.status === "success" && Array.isArray(memberRes.data)) {
        setAvailableMembers(memberRes.data);
      }
      if (configRes.status === "success" && configRes.result?.length > 0) {
        const cfg = configRes.result[0];
        setTeamConfig({
          min_size: cfg.min_size ?? 1,
          team_size: cfg.team_size ?? 15,
          teamname_regex: cfg.teamname_regex || ".*",
        });
      }
      // Pre-fill form with existing team data
      const existingMembers = (team.members || []).map((m: any) => ({
        user_id: m.user_id || m.id,
        id: m.user_id || m.id,
        name: m.name || m.user_name || "",
        email: m.email || m.user_email || "",
        role: m.team_role ?? m.role ?? 0,
      }));
      form.setFieldsValue({ name: team.team_name || team.name, members: existingMembers });
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMembers(false);
    }
  };

  // Handle save team (create or edit)
  const handleSaveTeam = async () => {
    try {
      const values = await form.validateFields();
      const memberCount = values.members?.length || 0;
      if (!isTaskAdmin && memberCount > teamConfig.team_size) {
        message.error(`จำนวนสมาชิกต้องไม่เกิน ${teamConfig.team_size} คน`);
        return;
      }
      // ถ้า leader เป็นสมาชิกคนสุดท้ายแล้วลบตัวเองออก → ลบทีม
      const originalMemberCount = (editingTeam?.members || []).length;
      if (memberCount === 0 && editingTeam && originalMemberCount === 1) {
        const teamId = editingTeam.team_id || editingTeam.id;
        setCreateLoading(true);
        try {
          const res = await team_delete({ task_id: taskId, team_id: teamId });
          if (res.status === "success") {
            message.success("ออกทีมและลบทีมสำเร็จ");
            closeModal();
            onRefresh?.();
          } else {
            message.error(res.message || "ไม่สามารถลบทีมได้");
          }
        } catch (e) {
          message.error("เกิดข้อผิดพลาดในการลบทีม");
        } finally {
          setCreateLoading(false);
        }
        return;
      }

      const leaderCount = values.members?.filter((m: any) => m.role === 1).length || 0;
      if (leaderCount !== 1) {
        message.error("ทีมต้องมีหัวหน้าเพียงคนเดียวเท่านั้น");
        return;
      }
      setCreateLoading(true);

      if (editingTeam) {
        // === EDIT MODE ===
        const teamId = editingTeam.team_id || editingTeam.id;
        const currentMemberIds = (editingTeam.members || []).map((m: any) => m.user_id || m.id);
        const newMemberIds = values.members?.map((m: any) => m.user_id || m.id) || [];
        const removedMemberIds = currentMemberIds.filter((id: string) => !newMemberIds.includes(id));
        const addedMemberIds = newMemberIds.filter((id: string) => !currentMemberIds.includes(id));
        const oldLeader = (editingTeam.members || []).find((m: any) => (m.team_role ?? m.role) === 1);
        const newLeader = values.members?.find((m: any) => m.role === 1);

        const promises: Promise<any>[] = [];
        if (values.name !== (editingTeam.team_name || editingTeam.name)) {
          promises.push(team_updateName({ task_id: taskId, team_id: teamId, team_name: values.name }));
        }
        for (const userId of removedMemberIds) {
          promises.push(team_deleteMember({ task_id: taskId, team_id: teamId, user_id: userId }));
        }
        if (addedMemberIds.length > 0) {
          promises.push(team_updateMember({ task_id: taskId, team_id: teamId, user_id: addedMemberIds }));
        }

        const results = await Promise.all(promises);
        if (results.length > 0 && !results.every((r) => r.status === "success")) {
          const failedResult = results.find((r) => r.status !== "success");
          message.error(failedResult?.message || "ไม่สามารถอัพเดททีมได้บางส่วน");
          closeModal();
          onRefresh?.();
          return;
        }

        // Change leader if needed
        const oldLeaderId = oldLeader?.user_id || oldLeader?.id;
        const newLeaderId = newLeader?.user_id || newLeader?.id;
        if (oldLeaderId && newLeaderId && oldLeaderId !== newLeaderId) {
          const isOldLeaderRemoved = removedMemberIds.includes(oldLeaderId);
          if (isOldLeaderRemoved) {
            await team_updateMemberRole({ task_id: taskId, team_id: teamId, user_id: newLeaderId, team_role: 1 });
          } else {
            await team_changeLeader({ task_id: taskId, team_id: teamId, leader_user_id: oldLeaderId, member_user_id: newLeaderId });
          }
        }

        message.success("อัพเดททีมสำเร็จ!");
        closeModal();
        onRefresh?.();
      } else {
        // === CREATE MODE ===
        const memberIds = values.members?.map((m: any) => m.user_id || m.id) || [];
        const res = await team_create({ task_id: taskId, team_name: values.name, user_id: memberIds });
        if (res.status === "success") {
          message.success("สร้างทีมสำเร็จ!");
          closeModal();
          onRefresh?.();
        } else {
          message.error(res.message || "ไม่สามารถสร้างทีมได้");
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <Row gutter={[16, 16]}>
      {/* ========== My Team Card ========== */}
      <Col xs={24}>
        <Card
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <CrownOutlined style={{ color: "#faad14" }} />
              <span>{t("My Team")}</span>
            </div>
          }
          style={{ height: "100%" }}
        >
          {userTeam ? (
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Card className="tab-member-card is-my-team" styles={{ body: { padding: 0 } }}>
                  {/* Header */}
                  <div className="tab-member-card-header">
                    <Tooltip title={userTeam.team_name || userTeam.name}>
                      <h3>{userTeam.team_name || userTeam.name}</h3>
                    </Tooltip>
                    <div className="tab-member-card-actions">
                      {(isUserLeaderOfTeam() || isAdminOrSuperAdmin()) && (
                        <Tooltip title="แก้ไขทีม">
                          <Button
                            shape="circle"
                            icon={<EditOutlined />}
                            className="btn-edit"
                            onClick={(e) => { e.stopPropagation(); openEditModal(userTeam); }}
                          />
                        </Tooltip>
                      )}
                    </div>
                  </div>
                  {/* Body */}
                  <div className="tab-member-card-body" onClick={() => handleTeamClick(userTeam)}>
                    {getLeaderEmailForTeam(userTeam) && (
                      <div className="tab-email-box">
                        <p>ต้องการเข้าทีมกรุณาติดต่อ :</p>
                        <p
                          className="tab-email-text"
                          onClick={(e) => handleCopyEmail(getLeaderEmailForTeam(userTeam)!, e)}
                          title="คลิกเพื่อคัดลอกอีเมล"
                        >
                          <MailOutlined /> {getLeaderEmailForTeam(userTeam)}
                        </p>
                      </div>
                    )}
                    <div className="tab-members-section">
                      <h4 className="tab-members-header">
                        <UserOutlined /> Member:
                      </h4>
                      <div className="tab-members-list">
                        {(userTeam.members || []).slice(0, 2).map((m: any) => (
                          <div key={m.user_id} className="tab-member-item">
                            <span className="tab-member-name">
                              <UserOutlined />{m.name}
                            </span>
                            <span className={`tab-member-role ${m.team_role === 1 ? "leader" : "member"}`}>
                              {m.team_role === 1 ? "Leader" : "Member"}
                            </span>
                          </div>
                        ))}
                        {(userTeam.members || []).length > 2 && (
                          <div className="tab-member-more">
                            +{(userTeam.members || []).length - 2} อื่นๆ — คลิกเพื่อดูทั้งหมด
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Footer */}
                  <div className="tab-member-card-footer">
                    {(() => {
                      const memberCount = userTeam.count_member || userTeam.members?.length || 0;
                      const leaderCannotLeave = isUserLeaderOfTeam() && memberCount > 1;
                      return leaderCannotLeave ? (
                        <Tooltip title="กรุณาให้สมาชิกทุกคนออกก่อน">
                          <Button danger icon={<ExportOutlined />} disabled>
                            {t("Leave team")}
                          </Button>
                        </Tooltip>
                      ) : (
                        <Button
                          // size="small"
                          danger
                          icon={<ExportOutlined />}
                          loading={leaveLoading}
                          onClick={(e) => { e.stopPropagation(); handleLeaveTeam(); }}
                        >
                          {t("Leave team")}
                        </Button>
                      );
                    })()}
                    <span className="tab-member-count">
                      {userTeam.count_member || 0} สมาชิก
                    </span>
                  </div>
                </Card>
              </Col>
            </Row>
          ) : (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <Empty
                description={t("You are not in any team")}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
              {currentUserId && isMember && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  style={{ marginTop: 16 }}
                  onClick={openCreateModal}
                >
                  {t("New Team")}
                </Button>
              )}
            </div>
          )}
        </Card>
      </Col>

      {/* ========== All Teams Card ========== */}
      <Col xs={24}>
        <Card
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <TeamOutlined />
              <span>{t("All Teams")}</span>
            </div>
          }
        >
          {memberDetail?.length > 0 ? (
            <Row gutter={[16, 16]}>
              {memberDetail.map((item: any, index: number) => {
                const isUserTeam =
                  userTeam &&
                  ((userTeam.team_id &&
                    item.team_id &&
                    userTeam.team_id === item.team_id) ||
                    (userTeam.id && item.id && userTeam.id === item.id));

                const leaderEmail = getLeaderEmailForTeam(item);
                return (
                  <Col
                    xs={24}
                    sm={12}
                    md={8}
                    lg={6}
                    key={item.team_id || item.id || index}
                  >
                    <Card
                      className={`tab-member-card${isUserTeam ? " is-my-team" : ""}`}
                      styles={{ body: { padding: 0 } }}
                    >
                      {/* Header */}
                      <div className="tab-member-card-header">
                        <Tooltip title={item.team_name || item.name}>
                          <h3>{item.team_name || item.name}</h3>
                        </Tooltip>
                        <div className="tab-member-card-actions">
                          {isAdminOrSuperAdmin() && (
                            <Tooltip title="แก้ไขทีม">
                              <Button
                                shape="circle"
                                icon={<EditOutlined />}
                                className="btn-edit"
                                onClick={(e) => { e.stopPropagation(); openEditModal(item); }}
                              />
                            </Tooltip>
                          )}
                          {isAdminOrSuperAdmin() && (
                            <Tooltip title="ลบทีม">
                              <Button
                                shape="circle"
                                icon={<DeleteOutlined />}
                                className="btn-delete"
                                onClick={(e) => handleAdminDeleteTeam(item, e)}
                              />
                            </Tooltip>
                          )}
                        </div>
                      </div>
                      {/* Body */}
                      <div className="tab-member-card-body" onClick={() => handleTeamClick(item)}>
                        {leaderEmail && (
                          <div className="tab-email-box">
                            <p>ต้องการเข้าทีมกรุณาติดต่อ :</p>
                            <p
                              className="tab-email-text"
                              onClick={(e) => handleCopyEmail(leaderEmail, e)}
                              title="คลิกเพื่อคัดลอกอีเมล"
                            >
                              <MailOutlined /> {leaderEmail}
                            </p>
                          </div>
                        )}
                        <div className="tab-members-section">
                          <h4 className="tab-members-header">
                            <UserOutlined /> Member:
                          </h4>
                          <div className="tab-members-list">
                            {(item.members || []).slice(0, 2).map((m: any) => (
                              <div key={m.user_id} className="tab-member-item">
                                <span className="tab-member-name">
                                  <UserOutlined />{m.name}
                                </span>
                                <span className={`tab-member-role ${m.team_role === 1 ? "leader" : "member"}`}>
                                  {m.team_role === 1 ? "Leader" : "Member"}
                                </span>
                              </div>
                            ))}
                            {(item.members || []).length > 2 && (
                              <div className="tab-member-more">
                                +{(item.members || []).length - 2} อื่นๆ — คลิกเพื่อดูทั้งหมด
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Footer */}
                      <div className="tab-member-card-footer">
                        {isUserTeam && (
                          <Tag color="success" style={{ margin: 0 , fontSize:"16px" }}>
                            <CrownOutlined style={{ marginRight: 4 , fontSize:"16px"}} />
                            {t("Your team")}
                          </Tag>
                        )}
                        {!isUserTeam && <span />}
                        <span className="tab-member-count">
                          {item.count_member || 0} สมาชิก
                        </span>
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          ) : (
            <Empty description={t("No team found")} />
          )}
        </Card>
      </Col>

      {/* ========== Team Detail Modal ========== */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <TeamOutlined />
            <span>รายละเอียดทีม</span>
          </div>
        }
        open={isDetailModalVisible}
        onCancel={handleCloseDetailModal}
        footer={[
          <Button key="cancel" onClick={handleCloseDetailModal}>
            {t("Close")}
          </Button>,
          isAdminOrSuperAdmin() && selectedTeam && !isUserInSelectedTeam() ? (
            <Button
              key="admin-edit"
              icon={<EditOutlined />}
              onClick={() => { handleCloseDetailModal(); openEditModal(selectedTeam); }}
            >
              แก้ไขทีม
            </Button>
          ) : null,
          isAdminOrSuperAdmin() && selectedTeam ? (
            <Button
              key="admin-delete"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => handleAdminDeleteTeam(selectedTeam, e)}
            >
              ลบทีม
            </Button>
          ) : null,
          isUserInSelectedTeam() ? (() => {
            const memberCount = selectedTeam?.count_member || selectedTeam?.members?.length || 0;
            const leaderCannotLeave = isUserLeaderOfSelectedTeam() && memberCount > 1;
            return leaderCannotLeave ? (
              <Tooltip title="กรุณาให้สมาชิกทุกคนออกก่อน" key="leave-disabled">
                <Button type="primary" danger icon={<ExportOutlined />} disabled>
                  {t("Leave team")}
                </Button>
              </Tooltip>
            ) : (
              <Button
                key="leave"
                type="primary"
                danger
                icon={<ExportOutlined />}
                loading={leaveLoading}
                onClick={handleLeaveTeam}
              >
                {t("Leave team")}
              </Button>
            );
          })() : null,
        ]}
        width={isMobile ? "95vw" : isTablet ? "85vw" : 900}
        className={`team-member-modal ${isMobile ? "team-member-modal-mobile" : ""}`}
        centered={isMobile}
      >
        {selectedTeam && (
          <div>
            <div className="team-info-header">
              <div className="team-name-row">
                <div>
                  <div className="team-name-label">ชื่อทีม:</div>
                  <div className="team-name-value">
                    {selectedTeam.team_name || selectedTeam.name}
                  </div>
                </div>
                {selectedTeam.count_member && (
                  <div className="member-count-badge">
                    <UserOutlined />
                    <span>{selectedTeam.count_member} สมาชิก</span>
                  </div>
                )}
              </div>

              {getLeaderEmail() && (
                <div className="leader-email-row">
                  <MailOutlined className="email-icon" />
                  <span className="email-label">Leader Email:</span>
                  <span
                    className="email-value"
                    onClick={(e) => handleCopyEmail(getLeaderEmail()!, e)}
                    title="คลิกเพื่อคัดลอกอีเมล"
                  >
                    {getLeaderEmail()}
                  </span>
                </div>
              )}
            </div>

            {selectedTeam.members && selectedTeam.members.length > 0 && (
              <div className="members-section">
                <div className="members-title">
                  <TeamOutlined />
                  <span>รายชื่อสมาชิก:</span>
                </div>
                <Table
                  dataSource={selectedTeam.members.map(
                    (m: any, idx: number) => ({ ...m, key: idx })
                  )}
                  pagination={false}
                  size={isMobile ? "small" : "middle"}
                  className="members-table"
                  scroll={{ x: isMobile ? 500 : undefined }}
                  columns={[
                    {
                      title: "#",
                      key: "number",
                      width: isMobile ? 40 : 60,
                      align: "center",
                      render: (_: any, __: any, index: number) => index + 1,
                    },
                    {
                      title: "ชื่อ",
                      dataIndex: "name",
                      key: "name",
                      align: "center",
                      width: isMobile ? 120 : undefined,
                      ellipsis: isMobile,
                    },
                    {
                      title: "อีเมล",
                      dataIndex: "email",
                      key: "email",
                      align: "center",
                      width: isMobile ? 180 : undefined,
                      ellipsis: isMobile,
                    },
                    {
                      title: "บทบาท",
                      dataIndex: "team_role",
                      key: "team_role",
                      align: "center",
                      width: isMobile ? 100 : undefined,
                      render: (role: number) => (
                        <span
                          className={`role-badge ${
                            role === 1 ? "leader" : "member"
                          }`}
                        >
                          {role === 1 ? "หัวหน้าทีม" : "สมาชิก"}
                        </span>
                      ),
                    },
                  ]}
                />
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* ========== Create / Edit Team Modal ========== */}
      <Modal
        title={editingTeam ? "Edit Team" : "New Team"}
        open={isCreateModalVisible}
        onOk={handleSaveTeam}
        onCancel={closeModal}
        okText="OK"
        cancelText="Cancel"
        confirmLoading={createLoading}
        width={700}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Team Name"
            rules={[
              { required: true, message: "Please input team name!" },
              {
                pattern: new RegExp(teamConfig.teamname_regex),
                message: `Team name must match the pattern: ${teamConfig.teamname_regex}`,
              },
            ]}
          >
            <Input placeholder="Enter team name" />
          </Form.Item>

          {loadingMembers ? (
            <div style={{ textAlign: "center", padding: 20 }}>
              <Spin tip="กำลังโหลดรายชื่อสมาชิก..." />
            </div>
          ) : (
            <Form.List name="members">
              {(fields, { add, remove }) => (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <label style={{ fontWeight: 500 }}>Members</label>
                      <span style={{ marginLeft: 8, color: "#666", fontSize: 12 }}>
                        (Min: {teamConfig.min_size} / Max: {teamConfig.team_size} members)
                      </span>
                    </div>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      // size="small"
                      disabled={
                        availableMembers.length === 0 ||
                        (!isTaskAdmin && fields.length >= teamConfig.team_size)
                      }
                    >
                      + Add Member
                    </Button>
                  </div>

                  {availableMembers.length === 0 && (
                    <div
                      style={{
                        padding: 16,
                        marginTop: 8,
                        background: "#f5f5f5",
                        borderRadius: 4,
                        textAlign: "center",
                        color: "#666",
                      }}
                    >
                      ไม่มีสมาชิกที่สามารถเพิ่มได้ในขณะนี้
                    </div>
                  )}

                  {fields.map(({ key, name, ...restField }) => {
                    const currentMember = form.getFieldValue("members")?.[name];
                    const isCurrentUser = !editingTeam && currentMember?.user_id === auth.user.user_id;
                    const isExistingMember = editingTeam && (editingTeam.members || []).some(
                      (m: any) => (m.user_id || m.id) === (currentMember?.user_id || currentMember?.id)
                    );
                    const isReadOnly = isCurrentUser || isExistingMember;

                    return (
                      <Row gutter={8} key={key} style={{ marginTop: 16 }} align="middle">
                        {isReadOnly ? (
                          <>
                            <Col span={16}>
                              <Input
                                disabled
                                value={
                                  isCurrentUser
                                    ? `${currentMember.name} (${currentMember.email}) - You`
                                    : `${currentMember.name} (${currentMember.email})`
                                }
                                style={{ backgroundColor: "#f0f0f0", color: "#666" }}
                              />
                            </Col>
                            <Col span={6}>
                              <Form.Item
                                {...restField}
                                name={[name, "role"]}
                                rules={[{ required: true, message: "Role" }]}
                                style={{ marginBottom: 0 }}
                              >
                                <Select placeholder="Role" style={{ width: "100%" }}>
                                  <Select.Option value={1}>Leader</Select.Option>
                                  <Select.Option value={0}>Member</Select.Option>
                                </Select>
                              </Form.Item>
                            </Col>
                          </>
                        ) : (
                          <>
                            <Col span={16}>
                              <Form.Item
                                {...restField}
                                name={[name, "user_id"]}
                                rules={[{ required: true, message: "กรุณาเลือกสมาชิก" }]}
                                style={{ marginBottom: 0 }}
                              >
                                <Select
                                  showSearch
                                  placeholder="ค้นหาและเลือกสมาชิก"
                                  optionFilterProp="children"
                                  filterOption={(input, option) =>
                                    (option?.label ?? "")
                                      .toLowerCase()
                                      .includes(input.toLowerCase())
                                  }
                                  options={availableMembers
                                    .filter((member: any) => {
                                      if (!editingTeam) return member.user_id !== auth.user.user_id;
                                      return true;
                                    })
                                    .map((member: any) => ({
                                      value: member.user_id,
                                      label: `${member.name} (${member.email})`,
                                      member,
                                    }))}
                                  onChange={(_value, option: any) => {
                                    if (option?.member) {
                                      const members = form.getFieldValue("members");
                                      members[name] = {
                                        ...members[name],
                                        user_id: option.member.user_id,
                                        name: option.member.name,
                                        email: option.member.email,
                                        id: option.member.user_id,
                                      };
                                      form.setFieldsValue({ members });
                                    }
                                  }}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={6}>
                              <Form.Item
                                {...restField}
                                name={[name, "role"]}
                                rules={[{ required: true, message: "Role" }]}
                                initialValue={0}
                                style={{ marginBottom: 0 }}
                              >
                                <Select placeholder="Role" style={{ width: "100%" }}>
                                  <Select.Option value={1}>Leader</Select.Option>
                                  <Select.Option value={0}>Member</Select.Option>
                                </Select>
                              </Form.Item>
                            </Col>
                          </>
                        )}
                        <Col span={2} style={{ textAlign: "center" }}>
                          {(!isCurrentUser || editingTeam) && (
                            <Button
                              // size="small"
                              icon={<DeleteOutlined style={{ color: "#B31312" }} />}
                              style={{ backgroundColor: "#FFDFDF" }}
                              onClick={() => remove(name)}
                            />
                          )}
                        </Col>
                      </Row>
                    );
                  })}
                </>
              )}
            </Form.List>
          )}
        </Form>
      </Modal>

      {/* ========== Custom Confirm Modal ========== */}
      <Modal
        open={confirmModal.visible}
        onCancel={closeConfirmModal}
        footer={null}
        centered
        width={380}
        closable={false}
      >
        <div style={{ textAlign: "center", padding: "16px 0" }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: "#ff4d4f",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <ExclamationCircleFilled style={{ color: "white", fontSize: 28 }} />
          </div>
          <div style={{ fontSize: 18, fontWeight: 600, margin: "0 0 16px" }}>
            {confirmModal.title}
          </div>
          <span>{confirmModal.description}</span>
          <br />
          <span style={{ fontSize: 16, fontWeight: 700 }}>
            {confirmModal.teamName}
          </span>
          <br />
          <span>ใช่หรือไม่ ?</span>
          <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 24 }}>
            <Button onClick={closeConfirmModal}>ยกเลิก</Button>
            <Button danger onClick={confirmModal.onConfirm}>
              ยืนยัน
            </Button>
          </div>
        </div>
      </Modal>
    </Row>
  );
};

export default TabMember;
